import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Pencil, Trash2, CalendarDays, XCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";
import { money, fmtDate } from "./ClientesModule";

const inputCls = "w-full h-9 rounded-md border border-white/15 bg-vnavy px-3 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-vteal-light/50";
const CATEGORIAS = ["Planillas", "Alquiler", "Servicios públicos", "Impuestos", "Marketing", "Proveedores", "Otros"];
const mesActual = () => new Date().toISOString().slice(0, 7);
const hoyStr = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({ fecha: hoyStr(), concepto: "", categoria: "Otros", monto: "", currency: "CRC" });

const fmtMes = (m) => {
  const dt = new Date(m + "-02T00:00:00");
  const s = dt.toLocaleDateString("es-CR", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export default function GastosModule() {
  const [month, setMonth] = useState(mesActual());
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/expenses?month=${month}`);
      setExpenses(data || []);
    } catch {
      toast.error("No se pudieron cargar los gastos.");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const grupos = useMemo(() => {
    const map = {};
    expenses.forEach((e) => { (map[e.fecha] = map[e.fecha] || []).push(e); });
    return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
  }, [expenses]);

  const sumPorMoneda = (lista) => {
    const t = { CRC: 0, USD: 0 };
    lista.forEach((e) => { t[e.currency === "USD" ? "USD" : "CRC"] += e.monto || 0; });
    return [t.CRC ? money(t.CRC, "CRC") : "", t.USD ? money(t.USD, "USD") : ""].filter(Boolean).join(" + ") || money(0, "CRC");
  };

  const submit = async () => {
    if (!form.concepto.trim()) { toast.error("Ingrese el concepto del gasto."); return; }
    if (!form.fecha) { toast.error("Ingrese la fecha del gasto."); return; }
    if (!(Number(form.monto) > 0)) { toast.error("Ingrese un monto mayor a cero."); return; }
    setSaving(true);
    const payload = { ...form, concepto: form.concepto.trim(), monto: Number(form.monto) };
    try {
      if (editingId) {
        const { data } = await api.put(`/admin/expenses/${editingId}`, payload);
        setExpenses((prev) => prev.map((e) => (e.id === editingId ? data : e)));
        toast.success("Gasto actualizado.");
      } else {
        const { data } = await api.post("/admin/expenses", payload);
        if (data.fecha.startsWith(month)) {
          setExpenses((prev) => [...prev, data].sort((a, b) => b.fecha.localeCompare(a.fecha)));
        } else {
          setMonth(data.fecha.slice(0, 7));
        }
        toast.success("Gasto registrado.");
      }
      setForm(emptyForm());
      setEditingId("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el gasto.");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (e) => {
    setEditingId(e.id);
    setForm({ fecha: e.fecha, concepto: e.concepto, categoria: e.categoria || "Otros", monto: String(e.monto), currency: e.currency === "USD" ? "USD" : "CRC" });
  };

  const remove = async (e) => {
    if (confirmDelete !== e.id) {
      setConfirmDelete(e.id);
      setTimeout(() => setConfirmDelete((c) => (c === e.id ? "" : c)), 4000);
      return;
    }
    setDeleting(true);
    try {
      await api.delete(`/admin/expenses/${e.id}`);
      setExpenses((prev) => prev.filter((x) => x.id !== e.id));
      if (editingId === e.id) { setEditingId(""); setForm(emptyForm()); }
      toast.success("Gasto eliminado.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar el gasto.");
    } finally {
      setDeleting(false);
      setConfirmDelete("");
    }
  };

  return (
    <div className="grid lg:grid-cols-[400px_1fr] gap-6" data-testid="expenses-module">
      {/* Formulario */}
      <div className="rounded-xl bg-vnavy-800 border border-white/10 p-6 space-y-4 self-start">
        <div className="flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-[0.15em] text-vteal-light font-semibold">
            {editingId ? "Editar gasto" : "Registrar gasto"}
          </p>
          {editingId && (
            <button onClick={() => { setEditingId(""); setForm(emptyForm()); }} data-testid="expense-cancel-edit"
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors">
              <XCircle className="h-3.5 w-3.5" /> Cancelar
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Fecha *</label>
            <input type="date" data-testid="expense-fecha" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className={`${inputCls} [color-scheme:dark]`} />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Categoría</label>
            <select data-testid="expense-categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              className={`${inputCls} [color-scheme:dark]`}>
              {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-400 mb-2 block">Concepto *</label>
          <input data-testid="expense-concepto" value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })}
            placeholder="Ej. Pago de alquiler de oficina" className={inputCls} />
        </div>

        <div>
          <label className="text-sm text-gray-400 mb-2 block">Moneda *</label>
          <div className="flex rounded-lg overflow-hidden border border-white/10">
            {[{ v: "CRC", label: "₡ Colones" }, { v: "USD", label: "$ Dólares" }].map((m) => (
              <button key={m.v} type="button" onClick={() => setForm({ ...form, currency: m.v })}
                data-testid={`expense-currency-${m.v.toLowerCase()}`}
                className={`flex-1 text-xs py-2 font-semibold transition-colors ${form.currency === m.v ? "bg-vteal-light text-vnavy" : "text-gray-400 hover:text-white"}`}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm text-gray-400 mb-2 block">Monto ({form.currency === "USD" ? "$" : "₡"}) *</label>
          <input type="number" min="0" data-testid="expense-monto" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })}
            placeholder="Ej. 150000" className={inputCls} />
        </div>

        <button onClick={submit} disabled={saving} data-testid="expense-submit"
          className="w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-vteal-light text-vnavy text-sm font-semibold hover:bg-white transition-colors disabled:opacity-60">
          <Plus className="h-4 w-4" /> {saving ? "Guardando..." : editingId ? "Guardar cambios" : "Agregar gasto"}
        </button>
      </div>

      {/* Lista del mes */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-3">
            <CalendarDays className="h-4 w-4 text-vteal-light" />
            <input type="month" data-testid="expenses-month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
              className={`${inputCls} w-auto [color-scheme:dark]`} />
          </div>
          <div className="rounded-lg bg-vnavy-800 border border-white/10 px-4 py-2" data-testid="expenses-total">
            <span className="text-[10px] uppercase tracking-[0.15em] text-gray-500 mr-2">Total {fmtMes(month)}</span>
            <span className="text-vteal-light font-semibold">{sumPorMoneda(expenses)}</span>
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500 py-12 text-center">Cargando gastos...</p>
        ) : grupos.length === 0 ? (
          <div className="text-center py-16 text-gray-500">
            <Wallet className="h-10 w-10 mx-auto mb-3 opacity-40" />
            No hay gastos registrados en {fmtMes(month)}.
          </div>
        ) : (
          <div className="space-y-4" data-testid="expenses-list">
            {grupos.map(([fecha, lista]) => (
              <div key={fecha} className="rounded-xl bg-vnavy-800 border border-white/10 overflow-hidden" data-testid={`expense-day-${fecha}`}>
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-vnavy/60">
                  <p className="text-white text-sm font-semibold">{fmtDate(fecha)}</p>
                  <p className="text-gray-400 text-xs">{lista.length} gasto{lista.length !== 1 ? "s" : ""} · <span className="text-vteal-light font-semibold">{sumPorMoneda(lista)}</span></p>
                </div>
                <div className="divide-y divide-white/5">
                  {lista.map((e) => (
                    <div key={e.id} className="flex items-center gap-3 px-5 py-3" data-testid={`expense-row-${e.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium truncate">{e.concepto}</p>
                        <Badge className="border bg-white/5 text-gray-400 border-white/10 mt-1">{e.categoria || "Otros"}</Badge>
                      </div>
                      <p className="text-white font-semibold shrink-0">{money(e.monto, e.currency)}</p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button onClick={() => startEdit(e)} data-testid={`expense-edit-${e.id}`} title="Editar"
                          className="p-2 rounded-lg border border-white/15 text-gray-400 hover:border-vteal-light hover:text-white transition-colors">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => remove(e)} disabled={deleting} data-testid={`expense-delete-${e.id}`} title="Eliminar"
                          className={`flex items-center gap-1.5 text-xs p-2 rounded-lg border transition-colors disabled:opacity-60 ${confirmDelete === e.id ? "border-red-500 bg-red-500/20 text-red-200 font-semibold" : "border-white/15 text-gray-400 hover:border-red-500/50 hover:text-red-300"}`}>
                          <Trash2 className="h-3.5 w-3.5" /> {confirmDelete === e.id ? "¿Confirmar?" : ""}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
